import {
  Card,
  CardBody,
  Row,
  Col,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import { useMemo, useState, React, useEffect, useContext } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { MainContext } from "context/MainContext.js";
import { FaCheckCircle, FaHourglassHalf } from "react-icons/fa";
import { BsFillXCircleFill } from "react-icons/bs";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";
import LoadingOverlay from "react-loading-overlay";
import { ToastContainer, toast } from "react-toastify";
import Moment from "moment";
import "moment-timezone";

const TaskList = () => {
  const history = useHistory();
  const location = useLocation();
  const privileges = useContext(MainContext);
  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["tasks.display"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
  }, []);

  const [params, setParams] = useState(location.state?.value ?? "");

  useEffect(() => {
    if (location.state?.showToast) {
      toast.success(location.state.message || "Success", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        theme: "colored",
      });

      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "status",
        sortBy: "status",
        Cell: (props) => {
          let icon;
          switch (props.value) {
            case "completed":
              icon = (
                <FaCheckCircle className="text-success tableIconFontSize" />
              );
              break;
            case "error":
              icon = (
                <BsFillXCircleFill className="text-danger tableIconFontSize" />
              );
              break;
            case "pending":
              icon = (
                <FaHourglassHalf className="text-warning tableIconFontSize" />
              );
              break;
            default:
              icon = props.value;
          }
          return <div>{icon}</div>;
        },
        flex: "0 1 5%",
      },
      {
        Header: "Type",
        accessor: "description",
        sortBy: "description",
        flex: "1 1 0",
      },
      {
        Header: "Scheduled Time",
        accessor: "scheduledTime",
        sortBy: "scheduledTime",
        Cell: (props) => {
          return (
            <div>
              {props.settings.general_convert_date_with_user_timezone === "true"
                ? Moment(props.value)
                    .tz(props.settings.user_timezone)
                    .format("yy-MM-DD hh:mm a")
                : Moment.utc(props.value).format("yy-MM-DD hh:mm a")}
            </div>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Target",
        accessor: "target",
        sortable: false,
        filterable: false,
        flex: "1 1 0",
        Cell: ({ value }) => {
          if (!value) return <div>—</div>;

          const parts = value.split(" / ");

          return (
            <div
              style={{
                fontSize: "0.90em",
                lineHeight: "1.5",
              }}
            >
              {parts.map((part, index) => {
                const colonIndex = part.indexOf(":");
                if (colonIndex === -1) return <div key={index}>{part}</div>;

                const label = part.substring(0, colonIndex + 1);
                const val = part.substring(colonIndex + 1).trim();

                return (
                  <div key={index}>
                    <span style={{ color: "#1d1d1d" }}>{label}</span>{" "}
                    <strong>{val}</strong>
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        Header: "Details",
        accessor: "details",
        sortable: false,
        filterable: false,
        flex: "1 1 0",
        Cell: ({ value }) => {
          if (!value) return <div>—</div>;

          let parsed;
          try {
            parsed = typeof value === "string" ? JSON.parse(value) : value;
          } catch {
            return <div>{value}</div>;
          }

          if (!Array.isArray(parsed)) return <div>{value}</div>;

          return (
            <div
              style={{
                fontSize: "0.90em",
                lineHeight: "1.3",
              }}
            >
              {parsed.map((entry, index) => {
                const [key, val] = Object.entries(entry)[0];

                const label = key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (s) => s.toUpperCase());

                return (
                  <div key={index}>
                    <span style={{ color: "#1d1d1d" }}>{label}:</span>{" "}
                    <strong>{String(val)}</strong>
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        Header: "Message",
        accessor: "message",
        sortBy: "message",
        flex: "1 1 0",
      },
      {
        Header: "Execution Time",
        accessor: "executionTime",
        sortBy: "executionTime",
        Cell: (props) => {
          return (
            <div>
              {props.value
                ? props.settings.general_convert_date_with_user_timezone ===
                  "true"
                  ? Moment(props.value)
                      .tz(props.settings.user_timezone)
                      .format("yy-MM-DD hh:mm a")
                  : Moment.utc(props.value).format("yy-MM-DD hh:mm a")
                : "-"}
            </div>
          );
        },
        flex: "1 1 0",
      },
    ],
    [],
  );

  return (
    <>
      <ToastContainer />

      <div className="content">
        <LoadingOverlay
          active={showLoading}
          spinner
          text="Loading"
          className={showLoading ? "loading" : ""}
        />
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </NavLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Scheduled Tasks</BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <ReactTable
                  query="/api/v1/task/list"
                  columns={columns}
                  param={params}
                  title="Tasks"
                  className="-striped -highlight primary-pagination"
                  forceRefresh={forceRefreshCount}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default TaskList;
